import type { HostFloorPlanTable } from 'restaurantAdmin/floorPlans/apiHelpers';
import {
  FloorPlan,
  type FloorPlanTablesRenderer,
} from '@components/floorPlan/FloorPlan';
import { type TableTimerDictionary } from '../state/ReservationServiceContext';
import { useReservationServiceSelectedSeatingContext } from '../state/ReservationServiceSelectedSeatingContext';
import { SeatedFloorPlanTableFactory } from './SeatedFloorPlanTableFactory';
import { DragEndEvent, useDndMonitor } from '@dnd-kit/core';
import { getWalkInAvailability } from 'restaurantAdmin/reservations/apiHelpers';
import { useWaitListContext } from '../sidePanel/waitList/state/WaitListContext';
import { WaitListEntry } from '../sidePanel/waitList/apiHelpers';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import {
  ServicePageModalType,
  useServicePageModalContext,
} from 'restaurantAdmin/context/ServicePageModalContext';
import { errorToast } from '@components/toasts/Toasts';
import { reportAppError } from '@shared/reportAppError';

export interface WalkInSeatModeFloorPlanProps {
  backgroundSrc?: string;
  className?: string;
  tables: HostFloorPlanTable[];
  tableTimersByFloorPlanTableId: TableTimerDictionary;
}

export const WalkInSeatModeFloorPlan = ({
  backgroundSrc,
  className,
  tables,
  tableTimersByFloorPlanTableId,
}: WalkInSeatModeFloorPlanProps) => {
  const restaurant = useRestaurant();

  const { selectedFloorPlanTables, toggleSelectedFloorPlanTable } =
    useReservationServiceSelectedSeatingContext();

  const calculateIsHighlighted = (floorPlanTable: HostFloorPlanTable) => {
    return selectedFloorPlanTables.includes(floorPlanTable);
  };

  const { selectedWaitListEntry, confirmSeatWaitListEntry } =
    useWaitListContext();
  const { openModal } = useServicePageModalContext();

  const onDropWaitListEntry = async (floorPlanTable: HostFloorPlanTable) => {
    try {
      const response = await getWalkInAvailability({
        floorPlanTableId: floorPlanTable.id,
        guestCount: (selectedWaitListEntry as WaitListEntry).guestCount,
        restaurantId: restaurant.id,
      });
      if (!response.hasAvailability) {
        openModal(ServicePageModalType.WalkInSeatingConflict, {
          handleConfirm: () => void confirmSeatWaitListEntry([floorPlanTable]),
        });
        return;
      }

      await confirmSeatWaitListEntry([floorPlanTable]);
    } catch (error) {
      errorToast({
        message: 'Failed to confirm seat availability. Please try again.',
      });
      reportAppError(error);
    }
  };

  useDndMonitor({
    onDragEnd(event: DragEndEvent) {
      const { active, over } = event;

      const floorPlanTable = over?.data.current
        ?.floorPlanTable as HostFloorPlanTable;

      if (
        over &&
        active &&
        over.data.current?.accepts.includes(active.data.current?.type)
      ) {
        const seatWaitListEntryOnDragEnd = () =>
          onDropWaitListEntry(floorPlanTable);
        void seatWaitListEntryOnDragEnd();
      }
    },
  });

  const floorPlanTablesRenderer: FloorPlanTablesRenderer = (tableIconScale) => (
    <>
      {tables.map((floorPlanTable) => (
        <SeatedFloorPlanTableFactory
          floorPlanTable={floorPlanTable}
          handleTableOnClick={toggleSelectedFloorPlanTable}
          key={floorPlanTable.id}
          timer={tableTimersByFloorPlanTableId[floorPlanTable.id] || null}
          tableIconScale={tableIconScale}
          calculateIsHighlighted={calculateIsHighlighted}
          enableSeatedTableClick
        />
      ))}
    </>
  );

  return (
    <FloorPlan
      backgroundSrc={backgroundSrc}
      className={className}
      floorPlanTablesRenderer={floorPlanTablesRenderer}
      scaleOnVerticalResize
    />
  );
};
